import { useCallback, useEffect, useRef, useState } from "react";
import { BookOpen, CheckCircle2, Lock, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { PortalShell } from "@/components/AppHeader";
import { YouTubeTracked } from "@/components/YouTubeTracked";
import { API, api, errMsg } from "@/lib/api";

const videoSrc = (url) => (url?.startsWith("/api/") ? `${API.replace(/\/api$/, "")}${url}?token=${localStorage.getItem("lux_token")}` : url);

const FileVideo = ({ v, testId, onPercent }) => {
  const [failed, setFailed] = useState(false);
  const onTime = (e) => { const el = e.currentTarget; if (el.duration) onPercent(Math.min(100, (el.currentTime / el.duration) * 100)); };
  return (
    <>
      <video data-testid={testId} controls playsInline preload="metadata" onTimeUpdate={onTime} onEnded={() => onPercent(100)} className="aspect-video w-full rounded-lg bg-black">
        <source src={videoSrc(v.url)} type={v.url.endsWith(".webm") ? "video/webm" : "video/mp4"} onError={v.fallback_url ? undefined : () => setFailed(true)} />
        {v.fallback_url && <source src={v.fallback_url} type="video/webm" onError={() => setFailed(true)} />}
      </video>
      {failed && <p className="text-sm text-red-300">Video unavailable — please contact support at 214-506-2525.</p>}
    </>
  );
};

const Video = ({ m, i, v, onProgress }) => {
  const sent = useRef(m.videos_watched?.[i] || 0);
  const report = (pct) => {
    if (pct - sent.current >= 10 || (pct >= 90 && sent.current < 90) || (pct >= 99.5 && sent.current < 99.5)) { sent.current = pct; onProgress(m.id, i, pct); }
  };
  const testId = `training-video-${m.order}${i ? `-${i + 1}` : ""}`;
  return (
    <div className="space-y-2">
      {v.kind === "youtube" ? <YouTubeTracked videoId={v.youtube_id} onPercent={report} testId={testId} /> : <FileVideo v={v} testId={testId} onPercent={report} />}
      <div className="flex justify-between gap-3 text-xs">
        <span className="min-w-0 truncate text-zinc-400">{v.title ? `${v.title} · ${v.channel}` : "Training video"}</span>
        <span data-testid={`training-video-watched-${m.order}-${i}`} className="shrink-0 text-zinc-500">{Math.round(m.videos_watched?.[i] || 0)}% played</span>
      </div>
    </div>
  );
};

const Quiz = ({ m, answers, setAnswer }) => (
  <div className="space-y-4" data-testid={`training-quiz-${m.order}`}>
    {m.questions.map((q, qi) => (
      <fieldset key={qi} className="space-y-2">
        <legend className="text-sm text-amber-100">{qi + 1}. {q.question}</legend>
        {q.options.map((o, oi) => (
          <label key={oi} className="flex items-center gap-2 text-sm text-zinc-300">
            <input type="radio" name={`${m.id}-${qi}`} data-testid={`quiz-${m.order}-${qi}-${oi}`} checked={answers[m.id]?.[qi] === oi} onChange={() => setAnswer(m.id, qi, oi)} /> {o}
          </label>
        ))}
      </fieldset>
    ))}
  </div>
);

const Module = ({ m, onProgress, answers, setAnswer }) => (
  <div className="lux-card min-w-0 space-y-4 p-5 sm:p-6" data-testid={`training-module-${m.order}`}>
    <div className="flex items-start justify-between gap-3">
      <div><p className="lux-eyebrow">Module {m.order}</p><h2 className="mt-1 text-xl text-amber-50">{m.title}</h2>
        {m.description && <p className="mt-1 text-sm text-zinc-400">{m.description}</p>}</div>
      <span data-testid={`training-watched-${m.order}`} className="shrink-0 text-xs text-zinc-400">{Math.round(m.watched_percent)}% {m.kind === "text" ? "read" : "watched"}</span>
    </div>
    {m.videos.map((v, i) => <Video key={`${m.id}-${i}`} m={m} i={i} v={v} onProgress={onProgress} />)}
    {m.kind === "text" && (
      <div className="space-y-3">
        <p data-testid={`training-text-${m.order}`} className="whitespace-pre-line rounded-lg border border-white/10 bg-white/[0.02] p-4 text-sm leading-relaxed text-zinc-300">{m.body}</p>
        {!m.quiz_unlocked && <button data-testid={`training-mark-read-${m.order}`} onClick={() => onProgress(m.id, 0, 100)} className="btn-outline-gold !py-2 text-sm"><BookOpen className="h-4 w-4" /> I've read the policies</button>}
      </div>
    )}
    {m.kind === "video" && m.videos.some((v) => v.kind === "youtube") && <p className="text-[11px] text-zinc-600">Third-party video, embedded from YouTube. Only seconds you actually play count — skipping ahead doesn't.</p>}
    {!m.quiz_unlocked ? (
      <p data-testid={`training-quiz-locked-${m.order}`} className="flex items-center gap-2 text-sm text-zinc-400"><Lock className="h-4 w-4 text-amber-400" /> Quiz unlocks after you {m.kind === "text" ? "read the policies" : `play 90% of ${m.videos.length > 1 ? "each video" : "the video"}`}.</p>
    ) : <Quiz m={m} answers={answers} setAnswer={setAnswer} />}
  </div>
);

export default function DriverTraining() {
  const [data, setData] = useState(null);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get("/driver/training").then(({ data }) => setData(data)), []);
  useEffect(() => { load(); }, [load]);
  const onProgress = async (id, video, pct) => {
    const { data: p } = await api.post(`/driver/training/${id}/progress`, { percent: pct, video });
    setData((d) => ({ ...d, modules: d.modules.map((m) => (m.id === id ? { ...m, watched_percent: p.watched_percent, quiz_unlocked: p.quiz_unlocked, videos_watched: p.videos_watched } : m)) }));
  };
  const setAnswer = (mid, qi, oi) => setAnswers((a) => { const arr = [...(a[mid] || [])]; arr[qi] = oi; return { ...a, [mid]: arr }; });
  const submit = async () => {
    setBusy(true);
    try { const { data: r } = await api.post("/driver/training/submit", { answers }); setResult(r); r.passed ? toast.success(r.message) : toast.error(r.message); load(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const allUnlocked = data?.modules.every((m) => m.quiz_unlocked);
  return (
    <PortalShell>
      <p className="lux-eyebrow">Required before driving</p>
      <h1 className="mt-2 text-3xl font-semibold text-amber-50 sm:text-4xl">Training</h1>
      {data && (
        <div className="mt-8 space-y-6">
          <p data-testid="training-status" className={`flex items-center gap-2 text-sm ${data.passed ? "text-emerald-300" : "text-zinc-400"}`}>
            {data.passed && <CheckCircle2 className="h-4 w-4" />}
            {data.passed ? `Training passed${data.score != null ? ` · score ${data.score}%` : ""}` : `${data.modules_watched}/${data.modules_total} modules complete · pass mark ${data.pass_score}%`}
          </p>
          {data.modules.map((m) => <Module key={m.id} m={m} onProgress={onProgress} answers={answers} setAnswer={setAnswer} />)}
          <div className="lux-card space-y-3 p-5 sm:p-6">
            <p className="lux-eyebrow">Final test</p>
            {result && <p data-testid="training-result" className={`text-sm font-medium ${result.passed ? "text-emerald-300" : "text-amber-500"}`}>{result.message}</p>}
            <button data-testid="training-submit-button" onClick={submit} disabled={busy || !allUnlocked} className="btn-gold">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Submit final test</button>
            {!allUnlocked && <p className="text-xs text-zinc-500">Complete every module to unlock the final test.</p>}
          </div>
        </div>
      )}
    </PortalShell>
  );
}
